import { useState } from 'react';
import { Link } from 'react-router';
import { IconChevronRight, IconCircleCheck } from '@tabler/icons-react';
import {
  Badge,
  EmptyState,
  SegmentedControl,
  cn,
  PRESS,
  formatDate,
} from 'escrowbuild-ui';

import { useT } from '@/i18n';
import { usePublishedPublications } from '@/i18n';
import { PROJECTS, participationsForInvestor } from '@/data/domain';

/* =============================================================================
   Investor portal — the published update feed
   -----------------------------------------------------------------------------
   The narrative an investor reads, one period at a time. The feed is built from
   `publishedPublications` and never from `PUBLICATIONS`: the queue that sits
   behind this screen holds drafts and updates still in review, and §11 is
   explicit that being entered into the system is not what makes content visible.
   ============================================================================= */

/** The signed-in investor — see the note in `PortalHomePage`. */
const INVESTOR_ID = 'prado';

const CARD = 'rounded-xs border border-line bg-surface shadow-hard-sm';
const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';
const ALL = 'all';

/** Newest first, over a COPY: the caller's list is a fresh one, so the sort
 *  cannot reach a memoised array another reader of it is holding. */
function newestFirst<T extends { publishedAt: string | null }>(rows: T[]): T[] {
  return rows.sort((a, b) => (b.publishedAt ?? '').localeCompare(a.publishedAt ?? ''));
}

function projectName(projectId: string, fallback: string): string {
  return PROJECTS.find((project) => project.id === projectId)?.name ?? fallback;
}

export function PortalUpdatesPage() {
  const [scope, setScope] = useState<string>(ALL);
  const t = useT();

  /* The investor's own released updates, in the reader's language. Read from the
     published-only filter, never from the full queue: the working set stays
     internal. */
  const releases = usePublishedPublications();

  const projectIds = new Set(participationsForInvestor(INVESTOR_ID).map((entry) => entry.projectId));
  const projects = PROJECTS.filter((project) => projectIds.has(project.id));
  const updates = newestFirst(
    releases.filter((publication) => projectIds.has(publication.projectId)),
  );

  const visible = scope === ALL ? updates : updates.filter((entry) => entry.projectId === scope);

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <p className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {t('shell.portal.tagline')}
        </p>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('portal.updates.title')}
        </h1>
        <p className="max-w-2xl text-sm text-body">{t('portal.updates.intro')}</p>
      </header>

      <div className="flex flex-col gap-4">
        <SegmentedControl
          label={t('portal.updates.filterLabel')}
          value={scope}
          onValueChange={setScope}
          options={[
            { value: ALL, label: t('portal.updates.bothDevelopments') },
            ...projects.map((project) => ({ value: project.id, label: project.name })),
          ]}
        />

        <p role="status" className="text-xs text-muted">
          {scope === ALL
            ? t('portal.updates.count', { count: visible.length })
            : t('portal.updates.countScoped', {
                count: visible.length,
                project: projectName(scope, t('portal.updates.developmentFallback')),
              })}.
        </p>

        {visible.length === 0 ? (
          <EmptyState
            variant="cards"
            title={t('portal.updates.emptyTitle')}
            description={t('portal.updates.emptyBody')}
            filtersActive={scope !== ALL}
            onClearFilters={() => setScope(ALL)}
          />
        ) : (
          <ul className="flex list-none flex-col gap-4 p-0">
            {visible.map((publication) => (
              <li key={publication.id}>
                <article className={cn(CARD, 'flex flex-col gap-4 p-5')}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-2xs uppercase tracking-label text-muted">
                        {projectName(publication.projectId, t('portal.updates.developmentFallback'))}{' '}
                        · {publication.period}
                      </p>
                      <h2 className="mt-1 font-display text-lg font-semibold uppercase tracking-title text-heading">
                        {publication.title}
                      </h2>
                    </div>
                    <Badge
                      tone="success"
                      variant="subtle"
                      size="sm"
                      icon={<IconCircleCheck size={12} aria-hidden="true" />}
                    >
                      {t('portal.updates.publishedBadge', {
                        date: formatDate(publication.publishedAt),
                      })}
                    </Badge>
                  </div>

                  <p className="max-w-3xl text-sm leading-relaxed text-body">{publication.body}</p>

                  <dl className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                    <div>
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.updates.photos')}
                      </dt>
                      <dd className="mt-0.5 text-body">{publication.photos}</dd>
                    </div>
                    <div>
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.updates.documents')}
                      </dt>
                      <dd className="mt-0.5 text-body">{publication.documents}</dd>
                    </div>
                    <div>
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.updates.approvedBy')}
                      </dt>
                      <dd className="mt-0.5 text-body">{publication.approvedBy}</dd>
                    </div>
                    <div>
                      <dt className="text-2xs uppercase tracking-label text-muted">
                        {t('portal.updates.period')}
                      </dt>
                      <dd className="mt-0.5 text-body">{publication.period}</dd>
                    </div>
                  </dl>

                  <Link
                    to={`/portal/updates/${publication.id}`}
                    className={cn(
                      'mt-1 inline-flex items-center gap-1.5 self-start rounded-xs text-sm font-medium text-primary no-underline hover:text-primary-hover',
                      PRESS,
                      FOCUS_RING,
                    )}
                  >
                    {t('portal.updates.readUpdate')}
                    <IconChevronRight size={14} aria-hidden="true" />
                  </Link>
                </article>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
